<template>
  <div class="max-w-4xl mx-auto space-y-6">
    <!-- Driver info -->
    <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
      <div class="flex items-center mb-6 space-x-1 text-gray-500">
        <span class="text-base material-icons-round">account_circle</span>
        <span class="text-xs font-semibold tracking-widest uppercase">Driver Info</span>
      </div>

      <div class="flex flex-col items-center gap-8 sm:flex-row sm:items-start">
        <img
          :src="driver.profileImage"
          v-image-preview
          :alt="driver.firstName"
          class="flex-shrink-0 object-cover w-40 h-40 p-1.5 border rounded-full shadow-sm bg-taxiye-100 border-taxiye-200"
        />

        <div class="grid flex-1 w-full grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
          <div>
            <p class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Name</p>
            <p class="mt-0.5 text-lg text-gray-900">{{ driver.firstName }} {{ driver.lastName }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Email</p>
            <p class="mt-0.5 text-lg text-gray-900">{{ driver.email || "N/A" }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Taxiye ID</p>
            <p class="mt-0.5 text-lg text-gray-900">{{ driver.driverId || "-" }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Contact Number</p>
            <p class="mt-0.5 text-lg text-gray-900">{{ driver.phoneNumber || "N/A" }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold tracking-wider text-gray-500 uppercase">City</p>
            <p class="mt-0.5 text-lg text-gray-900">{{ driver.city || "N/A" }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold tracking-wider text-gray-500 uppercase">Plate Number</p>
            <p class="mt-0.5 text-lg text-gray-900">{{ vehicle.plateNumber || "N/A" }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Subscription -->
    <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
      <div class="flex flex-wrap items-center justify-between gap-2 mb-5">
        <div class="flex items-center space-x-1 text-gray-500">
          <span class="text-base material-icons-round">card_membership</span>
          <span class="text-xs font-semibold tracking-widest uppercase">Subscription</span>
        </div>
        <button
          v-if="canGrant"
          class="inline-flex items-center h-10 gap-1 px-4 text-sm font-semibold text-white shadow-sm rounded-xl bg-taxiye-500 transition-colors duration-150 hover:bg-taxiye-600"
          @click="openGrant"
        >
          <span class="text-base material-icons-round">add_card</span>
          Grant Subscription
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <span
          v-if="subscriptionActive"
          class="inline-flex px-3 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full"
        >
          Active
        </span>
        <span
          v-else
          class="inline-flex px-3 py-1 text-xs font-semibold text-gray-700 bg-gray-200 rounded-full"
        >
          {{ driver.subscriptionExpiresAt ? "Expired" : "None" }}
        </span>
        <span v-if="subscriptionActive" class="text-sm text-gray-600">
          Expires {{ formatDate(driver.subscriptionExpiresAt) }}
        </span>
        <span v-else-if="driver.subscriptionExpiresAt" class="text-sm text-gray-600">
          Expired {{ formatDate(driver.subscriptionExpiresAt) }}
        </span>
      </div>
    </div>

    <!-- Legal info -->
    <div class="p-6 bg-white border border-gray-200 shadow-sm rounded-2xl">
      <div class="flex items-center mb-6 space-x-1 text-gray-500">
        <span class="text-base material-icons-round">gavel</span>
        <span class="text-xs font-semibold tracking-widest uppercase">Legal Info</span>
      </div>

      <div class="flex flex-wrap gap-8 mb-8">
        <div>
          <div class="flex items-center justify-center h-56 p-2 overflow-hidden border border-gray-200 rounded-xl bg-gray-50">
            <img
              v-image-preview
              :src="driver.businessLicense"
              alt="Driver Business License"
              class="h-full rounded-lg"
            />
          </div>
          <p class="mt-3 text-sm text-gray-600">Business License</p>
        </div>
        <div>
          <div class="flex items-center justify-center h-56 p-2 overflow-hidden border border-gray-200 rounded-xl bg-gray-50">
            <img
              v-image-preview
              :src="driver.drivingLicense"
              alt="Driver's License"
              class="h-full rounded-lg"
            />
          </div>
          <p class="mt-3 text-sm text-gray-600">Driver's License</p>
        </div>
      </div>
      <div
        v-if="driver.additionalDocuments && driver.additionalDocuments.length"
        class="mb-8"
      >
        <div class="flex flex-wrap gap-8">
          <div
            class="flex items-center justify-center h-56 p-2 overflow-hidden border border-gray-200 rounded-xl bg-gray-50"
            v-for="(document, i) in driver.additionalDocuments"
            :key="`additional-document-${i}`"
          >
            <img
              v-image-preview
              :src="document"
              alt="Driver's License"
              class="h-full rounded-lg"
            />
          </div>
        </div>
        <p class="mt-3 text-sm text-gray-600">Additional Documents</p>
      </div>
      <div v-if="driver.representationPaper">
        <p class="mb-3 text-sm text-gray-600">Representation Paper</p>
        <img
          v-image-preview
          :src="driver.representationPaper"
          alt="Driver Representation Paper"
          class="w-full border border-gray-200 rounded-xl"
        />
      </div>
    </div>

    <!-- Grant subscription modal -->
    <div
      v-if="granting"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="granting = false"
    >
      <div class="w-full max-w-md p-8 bg-white shadow-lg rounded-2xl">
        <div class="flex items-center mb-2 space-x-1 text-gray-500">
          <span class="text-base material-icons-round">card_membership</span>
          <span class="text-xs font-semibold tracking-widest uppercase">Grant Subscription</span>
        </div>
        <p class="mt-2 text-sm text-gray-600">
          {{
            subscriptionActive
              ? "This driver already has an active subscription — the new duration will be added on top of the current expiry (" +
                formatDate(driver.subscriptionExpiresAt) +
                "), not reset."
              : "This grants a subscription with no money moving. The driver will be charged 0% commission until it expires."
          }}
        </p>

        <label class="block mt-4">
          <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Duration (in days)</span>
          <input
            v-model="grant.durationDays"
            type="number"
            min="1"
            class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            placeholder="30"
          />
        </label>

        <label class="block mt-4">
          <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Amount (record-keeping only)</span>
          <div class="flex mt-1.5">
            <span
              class="flex items-center px-4 text-sm text-gray-600 border border-r-0 border-gray-300 bg-gray-100 rounded-l-xl"
            >
              ETB
            </span>
            <input
              v-model="grant.amount"
              type="number"
              min="0"
              class="w-full h-10 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-l-none rounded-r-xl focus:border-taxiye-300"
              placeholder="100"
            />
          </div>
        </label>

        <label class="block mt-4">
          <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Reason</span>
          <textarea
            v-model="grant.reason"
            class="w-full mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            rows="2"
            placeholder="Why is this subscription being granted?"
          ></textarea>
        </label>

        <div class="flex justify-end gap-3 mt-6">
          <button
            class="inline-flex items-center h-10 px-4 text-sm font-semibold text-gray-700 rounded-xl transition-colors duration-150 hover:bg-gray-100"
            @click="granting = false"
            :disabled="busy"
          >
            Cancel
          </button>
          <button
            class="inline-flex items-center h-10 px-5 text-sm font-semibold text-white shadow-sm rounded-xl transition-colors duration-150"
            :class="
              busy
                ? 'bg-taxiye-300 cursor-not-allowed'
                : 'bg-taxiye-500 hover:bg-taxiye-600'
            "
            :disabled="busy"
            @click="grantSubscription"
          >
            {{ subscriptionActive ? "Extend" : "Grant" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      driver: {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        profileImage: "",
        city: "",
        vehicleType: "",
        businessLicense: "",
        drivingLicense: "",
        representationPaper: "",
      },
      vehicle: {
        plateNumber: "",
      },
      subscriptionSettings: { amount: 100, durationDays: 30, enabled: false },
      granting: false,
      busy: false,
      grant: {
        durationDays: 30,
        amount: 100,
        reason: "",
      },
    };
  },
  created() {
    this.getDriver();
    this.getSubscriptionSettings();
  },
  computed: {
    subscriptionActive() {
      return (
        this.driver.subscriptionExpiresAt &&
        new Date(this.driver.subscriptionExpiresAt) > new Date()
      );
    },
    canGrant() {
      const role = this.$store.getters.role;
      return !!(
        role &&
        role.permissions &&
        role.permissions["drivers"] &&
        role.permissions["drivers"].canAccess
      );
    },
  },
  methods: {
    getDriver() {
      this.$misc
        .get("/drivers/" + this.$route.params.id)
        .then(response => {
          this.driver = response.data.driver;
          if (response.data.vehicle) this.vehicle = response.data.vehicle;
        })
        .catch(err => console.log(err));
    },
    getSubscriptionSettings() {
      this.$misc
        .get("/setting")
        .then(response => {
          if (response.data && response.data.subscriptionSettings) {
            this.subscriptionSettings = response.data.subscriptionSettings;
          }
        })
        .catch(err => console.log(err));
    },
    openGrant() {
      // Prefill from platform settings; admin can override before granting
      this.grant = {
        durationDays: this.subscriptionSettings.durationDays,
        amount: this.subscriptionSettings.amount,
        reason: "",
      };
      this.granting = true;
    },
    grantSubscription() {
      const durationDays = parseInt(this.grant.durationDays);
      if (isNaN(durationDays) || durationDays <= 0) {
        window.flash({
          type: "error",
          body: "You must send a valid durationDays!",
        });
        return;
      }
      const amount = Number(this.grant.amount);
      if (isNaN(amount) || amount < 0) {
        window.flash({ type: "error", body: "You must send a valid amount!" });
        return;
      }

      this.busy = true;
      this.$misc
        .post(`/drivers/${this.$route.params.id}/subscribe-admin`, {
          durationDays,
          amount,
          reason: this.grant.reason || undefined,
          account: this.$store.state.auth.account._id,
        })
        .then(response => {
          if (response.data && response.data.expiresAt) {
            this.driver.subscriptionExpiresAt = response.data.expiresAt;
          }
          this.granting = false;
          window.flash({
            type: "success",
            body: "Subscription granted successfully!",
          });
          this.getDriver();
        })
        .catch(err => {
          const message =
            err.response && err.response.data
              ? err.response.data.message || err.response.data
              : "Failed to grant subscription!";
          window.flash({ type: "error", body: message });
          console.log(err);
        })
        .finally(() => (this.busy = false));
    },
    formatDate(date) {
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
  },
};
</script>
